import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center border px-2.5 py-1 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground",
        outline: "text-foreground",
        brand: "border-transparent bg-brand-teal text-white",
        brand2: "border-transparent bg-brand-yellow text-[#061d4a]",
        success: "border-transparent bg-status-complete/10 text-status-complete",
        info: "border-transparent bg-blue-100 text-blue-700",
        warning: "border-transparent bg-brand-amber/10 text-brand-amber",
      },
      appearance: {
        solid: "",
        outlined: "",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        appearance: "outlined",
        class: "border-primary text-primary bg-transparent",
      },
      {
        variant: "destructive",
        appearance: "outlined",
        class: "border-destructive text-destructive bg-transparent",
      },
      {
        variant: "secondary",
        appearance: "outlined",
        class: "border-secondary text-secondary-foreground bg-transparent",
      },
      {
        variant: "brand",
        appearance: "outlined",
        class: "border-brand-teal text-brand-teal bg-transparent",
      },
      {
        variant: "brand2",
        appearance: "outlined",
        class: "border-brand-yellow text-[#061d4a] bg-transparent",
      },
      {
        variant: "success",
        appearance: "outlined",
        class: "border-status-complete text-status-complete bg-transparent",
      },
      {
        variant: "info",
        appearance: "outlined",
        class: "border-blue-400 text-blue-700 bg-transparent",
      },
      {
        variant: "warning",
        appearance: "outlined",
        class: "border-brand-amber text-brand-amber bg-transparent",
      },
    ],
    defaultVariants: {
      variant: "default",
      appearance: "solid",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof badgeVariants> {
  as?: React.ElementType;
  type?: string;
}

// Forwards its ref so the badge can be the child of an `asChild` wrapper —
// Radix primitives such as TooltipTrigger attach a ref to whatever they render
// as, and warn and lose the behaviour if the child can't accept one.
const Badge = React.forwardRef<HTMLElement, BadgeProps>(
  ({ className, variant, appearance, as: Comp = "div", ...props }, ref) => (
    <Comp ref={ref} className={cn(badgeVariants({ variant, appearance }), className)} {...props} />
  ),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
